import React, { useEffect, useState } from 'react';
import { api, ShreddingBinDto, BinScanEventDto, ShreddingBinSizeDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';
import { useBranding } from '../../context/BrandingContext';

const BIN_STATUSES = ['InService', 'AtCustomerSite', 'InTransitToDestruction', 'Destroyed', 'Retired'];

// ── Document Shredding — QR-tracked bins (admin) ─────────────────────────────
// A physically-labeled bin, scanned by contractors at placement/pickup/destruction to build a
// real chain-of-custody record — see BinScanEvent on the backend. Create a bin here, print its
// QR code, and affix it to the physical bin.

const fieldLabelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' };

const statusColor = (status: string) => status === 'Destroyed' ? C.textMuted : status === 'InService' ? C.textSub : C.greenMid;

export function ShreddingBinsPanel() {
    const [tab, setTab] = useState<'bins' | 'sizes'>('bins');
    const [bins, setBins] = useState<ShreddingBinDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [detailBin, setDetailBin] = useState<ShreddingBinDto | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getShreddingBins().then(setBins).catch(e => setError(e.message || 'Failed to load bins.')).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (showAdd) {
        return <AddBinForm onCancel={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />;
    }

    if (detailBin) {
        return <BinDetail bin={detailBin} onBack={() => { setDetailBin(null); load(); }} />;
    }

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Shredding Bins</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>QR-coded bins, scanned by contractors to build a chain-of-custody record</div>
                </div>
                {tab === 'bins' && <Btn onClick={() => setShowAdd(true)}>+ New bin</Btn>}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button onClick={() => setTab('bins')} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${tab === 'bins' ? C.greenMid : C.border}`, background: tab === 'bins' ? C.greenBg : '#fff', color: tab === 'bins' ? C.green : C.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Bins</button>
                <button onClick={() => setTab('sizes')} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${tab === 'sizes' ? C.greenMid : C.border}`, background: tab === 'sizes' ? C.greenBg : '#fff', color: tab === 'sizes' ? C.green : C.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Bin Sizes</button>
            </div>

            {tab === 'sizes' ? <SizesPanel /> : (
                <>
                    <Alert message={error} />
                    {loading ? <Spinner /> : (
                        <Card style={{ padding: 0, overflow: 'hidden' }}>
                            {bins.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No bins created yet.</div>}
                            {bins.map((b, i) => (
                                <div key={b.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                                    borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                                }}>
                                    <span style={{
                                        fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                        background: C.bg, color: statusColor(b.status),
                                        flexShrink: 0, minWidth: 110, textAlign: 'center',
                                    }}>{b.status}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{b.label}</div>
                                        {b.sizeName && <div style={{ fontSize: 12, color: C.textSub }}>{b.sizeName}</div>}
                                    </div>
                                    <Btn variant="outline" onClick={() => setDetailBin(b)}>View / print QR</Btn>
                                </div>
                            ))}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

function AddBinForm({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
    const [label, setLabel] = useState('');
    const [sizeId, setSizeId] = useState('');
    const [sizes, setSizes] = useState<ShreddingBinSizeDto[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.getShreddingBinSizesAdmin().then(all => setSizes(all.filter(s => s.isActive))).catch(() => setSizes([]));
    }, []);

    const save = async () => {
        if (!label.trim()) { setError('Label is required.'); return; }
        if (!sizeId) { setError('Please select the bin\'s size.'); return; }
        setSaving(true); setError('');
        try {
            await api.createShreddingBin(label.trim(), sizeId);
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 420, padding: 28 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: C.text }}>New bin</h2>
            <Alert message={error} />
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Label</label>
                <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Bin #014" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Size</label>
                {sizes.length === 0 ? (
                    <div style={{ fontSize: 12, color: C.textMuted }}>No bin sizes set up yet — add one under the "Bin Sizes" tab first.</div>
                ) : (
                    <select value={sizeId} onChange={e => setSizeId(e.target.value)} style={inputStyle}>
                        <option value="">Select a size…</option>
                        {sizes.map(s => <option key={s.id} value={s.id}>{s.name} — ${s.price.toFixed(2)}</option>)}
                    </select>
                )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Btn>
            </div>
        </Card>
    );
}

// ── Bin size catalog — named, flat-priced tiers (e.g. "32-gallon console"), each tagged which
// customer type(s) it's offered to. Mirrors the Dumpster size-tier CRUD pattern.
function SizesPanel() {
    const [sizes, setSizes] = useState<ShreddingBinSizeDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState<ShreddingBinSizeDto | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getShreddingBinSizesAdmin().then(setSizes).catch(e => setError(e.message || 'Failed to load sizes.')).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    return (
        <div>
            {(showAdd || editing) ? (
                <SizeForm
                    existing={editing}
                    onCancel={() => { setShowAdd(false); setEditing(null); }}
                    onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
                />
            ) : (
                <>
                    <div style={{ marginBottom: 12 }}><Btn onClick={() => setShowAdd(true)}>+ New size</Btn></div>
                    <Alert message={error} />
                    {loading ? <Spinner /> : (
                        <Card style={{ padding: 0, overflow: 'hidden' }}>
                            {sizes.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No bin sizes set up yet.</div>}
                            {sizes.map((s, i) => (
                                <div key={s.id} onClick={() => setEditing(s)} style={{
                                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer',
                                    borderTop: i === 0 ? 'none' : `1px solid ${C.border}`, opacity: s.isActive ? 1 : 0.5,
                                }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.name}{!s.isActive && ' (inactive)'}</div>
                                        <div style={{ fontSize: 12, color: C.textSub }}>
                                            {[s.availableToResidential && 'Residential', s.availableToBusiness && 'Business'].filter(Boolean).join(' · ') || 'Not available to anyone'}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>${s.price.toFixed(2)}</div>
                                </div>
                            ))}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

function SizeForm({ existing, onCancel, onSaved }: { existing: ShreddingBinSizeDto | null; onCancel: () => void; onSaved: () => void }) {
    const [name, setName] = useState(existing?.name ?? '');
    const [price, setPrice] = useState(existing ? String(existing.price) : '');
    const [availableToResidential, setAvailableToResidential] = useState(existing?.availableToResidential ?? true);
    const [availableToBusiness, setAvailableToBusiness] = useState(existing?.availableToBusiness ?? true);
    const [isActive, setIsActive] = useState(existing?.isActive ?? true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        if (!name.trim()) { setError('Name is required.'); return; }
        const priceNum = parseFloat(price);
        if (isNaN(priceNum) || priceNum < 0) { setError('Please enter a valid price.'); return; }
        setSaving(true); setError('');
        try {
            if (existing) {
                await api.updateShreddingBinSize(existing.id, { name: name.trim(), price: priceNum, availableToResidential, availableToBusiness, isActive });
            } else {
                await api.createShreddingBinSize({ name: name.trim(), price: priceNum, availableToResidential, availableToBusiness });
            }
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 420, padding: 28 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: C.text }}>{existing ? 'Edit size' : 'New size'}</h2>
            <Alert message={error} />
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 32-gallon console" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Price $</label>
                <input type="number" value={price} onChange={e => setPrice(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text }}>
                    <input type="checkbox" checked={availableToResidential} onChange={e => setAvailableToResidential(e.target.checked)} />
                    Available to residential customers
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text }}>
                    <input type="checkbox" checked={availableToBusiness} onChange={e => setAvailableToBusiness(e.target.checked)} />
                    Available to business customers
                </label>
                {existing && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text }}>
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                        Active
                    </label>
                )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
            </div>
        </Card>
    );
}

function BinDetail({ bin: initialBin, onBack }: { bin: ShreddingBinDto; onBack: () => void }) {
    const branding = useBranding();
    const [bin, setBin] = useState(initialBin);
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [scans, setScans] = useState<BinScanEventDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [savingStatus, setSavingStatus] = useState(false);
    const printFrameRef = React.useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        let activeQrUrl: string | null = null;
        setLoading(true); setError('');
        Promise.all([
            api.getShreddingBinQrCodeUrl(bin.id).then(url => { activeQrUrl = url; setQrUrl(url); }),
            api.getShreddingBinScans(bin.id).then(setScans),
        ]).catch(e => setError(e.message || 'Failed to load bin details.')).finally(() => setLoading(false));
        return () => { if (activeQrUrl) URL.revokeObjectURL(activeQrUrl); };
    }, [bin.id]);

    const changeStatus = async (status: string) => {
        setSavingStatus(true); setError('');
        try {
            const updated = await api.updateShreddingBinStatus(bin.id, status);
            setBin(updated);
        } catch (e: any) { setError(e.message || 'Failed to update status.'); }
        finally { setSavingStatus(false); }
    };

    // Printed via a same-document hidden iframe rather than window.open — a blob: object URL
    // (what qrUrl is) isn't reliably accessible from a separate browsing context opened with
    // window.open across browsers, but it works fine inside an iframe in the same document.
    const printLabel = () => {
        const frame = printFrameRef.current;
        if (!frame || !qrUrl) return;
        const doc = frame.contentWindow?.document;
        if (!doc) return;
        const appName = branding.appName || 'BoxRunner';
        doc.open();
        doc.write(`
            <html>
            <head><title>${bin.label}</title></head>
            <body style="text-align:center; font-family: sans-serif; margin: 0; padding: 24px;">
                ${branding.logoUrl ? `<img src="${branding.logoUrl}" style="height: 36px; margin-bottom: 8px;" />` : ''}
                <div style="font-size: 16px; font-weight: 700; margin-bottom: 4px;">${bin.label}</div>
                ${bin.sizeName ? `<div style="font-size: 13px; color: #333; margin-bottom: 4px;">${bin.sizeName}</div>` : ''}
                <img src="${qrUrl}" style="width: 240px; height: 240px; margin: 8px 0;" />
                <div style="font-size: 11px; color: #555; margin-top: 6px;">Property of ${appName}&trade; — if found, please return</div>
            </body>
            </html>
        `);
        doc.close();
        frame.onload = () => { frame.contentWindow?.focus(); frame.contentWindow?.print(); };
        // Some browsers fire onload before the image inside finishes painting — print after a
        // short delay too, as a fallback for those.
        setTimeout(() => { frame.contentWindow?.focus(); frame.contentWindow?.print(); }, 300);
    };

    return (
        <div style={{ maxWidth: 600 }}>
            <Btn variant="outline" onClick={onBack}>← Back to bins</Btn>
            <Card style={{ marginTop: 16, padding: 28 }}>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: C.text }}>{bin.label}</h2>
                {bin.sizeName && <div style={{ fontSize: 13, color: C.textSub, marginBottom: 10 }}>{bin.sizeName}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                    <select
                        value={bin.status}
                        onChange={e => changeStatus(e.target.value)}
                        disabled={savingStatus}
                        style={{ fontSize: 13, color: statusColor(bin.status), fontWeight: 600, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}` }}
                    >
                        {BIN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {savingStatus && <span style={{ fontSize: 12, color: C.textSub }}>Saving…</span>}
                </div>

                <Alert message={error} />
                {loading ? <Spinner /> : (
                    <>
                        {qrUrl && (
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <img src={qrUrl} alt={`QR code for ${bin.label}`} style={{ width: 220, height: 220 }} />
                                <div style={{ marginTop: 10 }}>
                                    <Btn variant="outline" onClick={printLabel}>🖨️ Print label</Btn>
                                </div>
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 8 }}>Print and affix to the physical bin</div>
                            </div>
                        )}
                        <iframe ref={printFrameRef} title="Bin QR print" style={{ display: 'none' }} />

                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Scan history</div>
                        {scans.length === 0 ? (
                            <div style={{ fontSize: 13, color: C.textMuted }}>No scans recorded yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {scans.map(s => (
                                    <div key={s.id} style={{ fontSize: 12, color: C.textSub, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                                        <strong style={{ color: C.text }}>{s.scanType}</strong> — {new Date(s.scannedAt).toLocaleString()} — {s.contractorName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
